import { useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, CheckCircle2, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { FieldError } from "@/components/FormFeedback";
import { validateEmail, normalizeEmail, validateMobile, cleanMobile } from "@/lib/validation";
import { cn } from "@/lib/utils";

const BENEFITS = [
  "AI Patient Timeline",
  "AI Lab Report Analysis",
  "Follow-up & Appointment Management",
  "Smart Patient Search",
  "Disease Progress Tracking",
  "Role-Based Access",
];

export default function ContactSalesPage() {
  const [form, setForm] = useState({ first_name: "", last_name: "", clinic_name: "", email: "", mobile: "" });
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const fieldErrors = {
    first_name: !form.first_name.trim() ? "First name is required" : null,
    clinic_name: !form.clinic_name.trim() ? "Clinic name is required" : null,
    email: validateEmail(form.email),
    mobile: validateMobile(form.mobile),
  };
  const isValid = Object.values(fieldErrors).every((e) => !e);

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ first_name: true, clinic_name: true, email: true, mobile: true });
    if (!isValid) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/leads", { ...form, email: normalizeEmail(form.email), mobile: cleanMobile(form.mobile) });
      setSubmitted(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="demo-success-page">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-700">
            <PartyPopper className="h-7 w-7" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-slate-900">Thank You!</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Your demo request has been received. Our team will contact you shortly to schedule your personalized RootLyst demonstration.
          </p>
          <Link to="/" className="mt-6 inline-block text-sm font-medium text-teal-700 hover:underline">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col justify-center bg-slate-900 px-8 py-16 text-white sm:px-16">
        <Link to="/" className="mb-10 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-600 text-white">
            <Leaf className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold">RootLyst</span>
        </Link>
        <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">The AI Clinical Assistant for Modern Specialist Clinics</h1>
        <p className="mt-4 max-w-md text-slate-300">
          RootLyst helps specialist clinics organize patient history, analyze medical reports, monitor disease
          progression, and improve follow-up care with AI-powered clinical workflows.
        </p>
        <div className="mt-8 space-y-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-center gap-3">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-400" />
              <span className="text-sm text-slate-200">{b}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 px-6 py-16 sm:px-16">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-semibold text-slate-900">Book a Personalized Demo</h2>
          <p className="mt-2 text-sm text-slate-600">Please fill out the form below. Our product specialist will contact you within one business day.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-sales-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input data-testid="cs-first-name" required value={form.first_name} onBlur={() => markTouched("first_name")} className={cn(touched.first_name && fieldErrors.first_name && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("first_name", e.target.value)} />
                {touched.first_name && <FieldError testId="cs-first-name-error" message={fieldErrors.first_name} />}
              </div>
              <div><Label>Last Name</Label><Input data-testid="cs-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
            </div>
            <div>
              <Label>Clinic Name *</Label>
              <Input data-testid="cs-clinic-name" required value={form.clinic_name} onBlur={() => markTouched("clinic_name")} className={cn(touched.clinic_name && fieldErrors.clinic_name && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("clinic_name", e.target.value)} />
              {touched.clinic_name && <FieldError testId="cs-clinic-name-error" message={fieldErrors.clinic_name} />}
            </div>
            <div>
              <Label>Email Address *</Label>
              <Input data-testid="cs-email" type="email" required value={form.email} onBlur={() => markTouched("email")} className={cn(touched.email && fieldErrors.email && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("email", e.target.value)} />
              {touched.email && <FieldError testId="cs-email-error" message={fieldErrors.email} />}
            </div>
            <div>
              <Label>Mobile Number *</Label>
              <Input data-testid="cs-mobile" required maxLength={10} value={form.mobile} onBlur={() => markTouched("mobile")} className={cn(touched.mobile && fieldErrors.mobile && "border-red-500 focus-visible:ring-red-500")} onChange={(e) => set("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))} />
              {touched.mobile && <FieldError testId="cs-mobile-error" message={fieldErrors.mobile} />}
            </div>
            {error && <p data-testid="contact-sales-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="cs-submit-btn" type="submit" disabled={loading || !isValid} className="w-full bg-teal-700 hover:bg-teal-800">
              {loading ? "Submitting..." : "Book My Demo"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
